// Discard-confirm guard for the composer (docs/06-ux-rules.md "Composer" /
// "Discard-confirm"; P6 D10): switching the selected item while the
// composer has unsent text or a reward/points selection must confirm
// first, and "Keep editing" has to leave the ORIGINAL item selected — which
// means the selection change itself has to be intercepted BEFORE it
// happens, not reacted to after the fact via a `detail.id` effect. Lives in
// the container layer (not inside `InboxComposer`) so it can wrap
// `selectItem` directly; the composer only reports its own dirty state via
// `setDirty`.
import { useCallback, useMemo, useRef, useState } from 'react'

export interface UseDiscardGuardResult {
  // Whether the confirm dialog should be shown.
  isOpen: boolean
  // The composer's current dirty state, as last reported via `setDirty`.
  hasUnsentDraft: boolean
  // Wired to the composer's `onDirtyChange` prop.
  setDirty: (dirty: boolean) => void
  // Wraps a selection change: runs `next()` immediately when there's
  // nothing to lose, otherwise defers it and opens the confirm dialog.
  confirmDiscard: (next: () => void) => void
  // "Discard" — runs the deferred selection change and clears dirty state.
  discard: () => void
  // "Keep editing" — closes the dialog; the deferred selection never runs,
  // so the original item stays selected.
  keepEditing: () => void
}

export function useDiscardGuard(): UseDiscardGuardResult {
  const [isOpen, setIsOpen] = useState(false)
  const [hasUnsentDraft, setHasUnsentDraft] = useState(false)
  const pendingRef = useRef<(() => void) | null>(null)

  const setDirty = useCallback((dirty: boolean) => {
    setHasUnsentDraft(dirty)
  }, [])

  const confirmDiscard = useCallback(
    (next: () => void) => {
      if (!hasUnsentDraft) {
        next()
        return
      }
      pendingRef.current = next
      setIsOpen(true)
    },
    [hasUnsentDraft]
  )

  const discard = useCallback(() => {
    setIsOpen(false)
    setHasUnsentDraft(false)
    const next = pendingRef.current
    pendingRef.current = null
    next?.()
  }, [])

  const keepEditing = useCallback(() => {
    setIsOpen(false)
    pendingRef.current = null
  }, [])

  // Memoized so a caller destructuring a single method (e.g. `confirmDiscard`
  // or `setDirty`) into its own `useCallback`/effect deps gets a stable
  // outer object too — see FeedbackInbox/index.tsx's `renderComposer` and
  // `handleSelectItem`, which each depend on the whole `discardGuard`
  // object per the repo's context-memoization convention.
  return useMemo(
    () => ({
      isOpen,
      hasUnsentDraft,
      setDirty,
      confirmDiscard,
      discard,
      keepEditing,
    }),
    [isOpen, hasUnsentDraft, setDirty, confirmDiscard, discard, keepEditing]
  )
}
